import { useTheme } from "@mui/material";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import type { JSX } from "react";
import { useEffect, useRef } from "react";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

interface MapComponentProps {
  markerCoord?: [number, number];
}

export const MapComponent = ({ markerCoord }: MapComponentProps): JSX.Element => {
  const theme = useTheme();

  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const initialCoordRef = useRef(markerCoord);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    //! Temporarily suppress console warnings for known MapBox issues
    const originalWarn = console.warn;
    console.warn = (...args: unknown[]): void => {
      const msg = args[0]?.toString() || "";
      if (
        msg.includes("sizerank") ||
        msg.includes("Failed to evaluate expression") ||
        msg.includes("featureNamespace") ||
        msg.includes("Expected value to be of type number")
      ) {
        return; // Suppress the specific warning
      }
      originalWarn.apply(console, args);
    };

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/sprengnetter-avm/cmafia2gk00uz01sl2hty8pj6",
      center: initialCoordRef.current || [13.405, 52.52],
      zoom: 17,
      pitch: 40,
    });

    //! Restore console.warn after map initialization
    mapRef.current.on("load", () => {
      console.warn = originalWarn;
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl());

    return (): void => {
      console.warn = originalWarn;
      mapRef.current?.remove(); // Cleanup on unmount
      mapRef.current = null;
    };
  }, []);

  // Handle marker updates separately
  useEffect(() => {
    if (!mapRef.current) return;

    // Remove existing marker if any
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    // Add new marker if coordinates provided
    if (markerCoord) {
      markerRef.current = new mapboxgl.Marker({
        color: theme.palette.primary.main,
      })
        .setLngLat(markerCoord)
        .addTo(mapRef.current);

      // Fly to new location
      mapRef.current.flyTo({
        center: markerCoord,
        zoom: 17,
        duration: 1000,
      });
    }
  }, [markerCoord, theme.palette.primary.main]);

  useEffect((): (() => void) => {
    if (!mapRef.current || !mapContainerRef.current) return () => {};

    const observer = new ResizeObserver(() => {
      // Add a small delay to ensure the container has fully resized
      requestAnimationFrame(() => {
        mapRef.current?.resize();
      });
    });

    observer.observe(mapContainerRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={mapContainerRef}
      style={{ width: "100%", height: "100%" }} // Ensure the map container has dimensions
    />
  );
};
